import React, { useRef } from 'react';
import 'react-day-picker/lib/style.css';
import 'moment/locale/fi';
import 'moment/locale/sv';
import { ClassValue } from 'classnames/types';
import styles from './DatePickerWithRange.module.scss';
import DatePicker, { DatePickerProps } from '../DatePicker/DatePicker';
import Icon from '../Icon/Icon';
import {
  dayPickerInputDefaultClasses,
  extendClassNames,
} from '../DatePicker/DatePicker.defaultClassNames.js';

import Div from '../Element/Div/Div';
import Span from '../Element/Span/Span';
import BodyText from '../Typography/BodyText/BodyText';
import Gutter from '../Gutter/Gutter';
import Flex from '../Flex/Flex';
import InputMessage from '../Input/InputMessage/InputMessage';

type DatePickerWithRangeProps = {
  /** DatePicker props for starting date picker */
  fromDatePicker: DatePickerProps;
  /** DatePicker props for end date picker */
  toDatePicker: DatePickerProps;
  /** Hide the existing label in order to show customized label */
  showCustomLabel?: boolean;
  /** Label shown on the input */
  label?: string;
  /** Custom class name  */
  className?: ClassValue;
  /** Error text shown below the input to indicate the errorneous state */
  errorText?: string;
  /** Additional description for input, if label is not enough */
  helperText?: string;
  /** Sets the input to disabled state */
  disabled?: boolean;
  /** Adds the required mark on the input */
  required?: boolean;
};

const dayPickerInputClassNamesToBeAppended = {
  overlayWrapper: styles.datePickerWithRange_overlayWrapper,
};
const dayPickerInputClassNames = extendClassNames(
  dayPickerInputDefaultClasses,
  dayPickerInputClassNamesToBeAppended
);

const DatePickerWithRange: React.FunctionComponent<DatePickerWithRangeProps> = ({
  fromDatePicker,
  toDatePicker,
  showCustomLabel,
  label,
  className,
  errorText,
  helperText,
  disabled,
  required,
  ...props
}) => {
  const fromDatePickerRef = useRef<HTMLInputElement>(null);
  const toDatePickerRef = useRef<HTMLInputElement>(null);

  const fromDate = fromDatePicker.value;
  const toDate = toDatePicker.value;

  /* istanbul ignore next */
  const focusToDatePicker = () => {
    /* istanbul ignore else */
    if (toDatePickerRef && toDatePickerRef.current) {
      toDatePickerRef.current.focus();
    }
  };

  const handleFromDateClick = () => {
    // Hack to fix resetting ref on render
    // https://stackoverflow.com/questions/35522220/react-ref-with-focus-doesnt-work-without-settimeout-my-example
    window.setTimeout(focusToDatePicker, 0);
  };

  /* istanbul ignore next */
  const selectedDays =
    fromDate && toDate ? [fromDate, { from: fromDate, to: toDate }] : undefined;

  const modifiers = {
    [styles.datePickerWithRange_rangeStart]: fromDate,
    [styles.datePickerWithRange_rangeEnd]: toDate,
  };

  const resolveLabel = label ? (
    required ? (
      <>
        <Span>{label} </Span>
        <Span className={styles.datePickerWithRange_required}>*</Span>
      </>
    ) : (
      label
    )
  ) : (
    <Span>&nbsp;</Span>
  );

  return (
    <Div
      {...props}
      className={[styles.datePickerWithRange, className]}
      inlineBlock
    >
      {!showCustomLabel && (
        <>
          <BodyText
            size="sm"
            block
            color={disabled ? 'gray400' : 'black'}
            tagName="label"
            htmlFor={fromDatePicker?.id}
          >
            {resolveLabel}
          </BodyText>
          <Gutter size="xs" />
        </>
      )}
      <Flex
        occupyVerticalSpace={false}
        className={[
          styles.datePickerWithRange_inputContainer,
          {
            [styles.datePickerWithRange_inputContainer__error]:
              !!errorText || errorText === ' ',
            [styles.datePickerWithRange_inputContainer__disabled]: !!disabled,
          },
        ]}
      >
        <DatePicker
          {...fromDatePicker}
          disabled={disabled}
          showCustomLabel={true}
          className={styles.datePickerWithRange_fromDatePicker}
          dayPickerProps={{
            selectedDays: selectedDays,
            disabledDays: toDate && { after: toDate },
            numberOfMonths: 2,
            modifiers: modifiers,
            inputClassNames: {
              ...dayPickerInputClassNames,
            },
            onDayClick: handleFromDateClick,
            initialMonth: toDate,
          }}
          onDayChange={fromDatePicker.onChange}
          inputRef={fromDatePickerRef}
        />
        <label>
          <Flex
            centeredVertically
            centeredHorizontally
            className={styles.datePickerWithRange_spacer}
          >
            <Icon
              data-testid="separator-icon"
              aria-label={label}
              name={'chevron-right'}
              size={'xs'}
              color={disabled ? 'gray400' : 'black'}
            />
          </Flex>
        </label>
        <DatePicker
          {...toDatePicker}
          disabled={disabled}
          showCustomLabel={true}
          className={styles.datePickerWithRange_toDatePicker}
          dayPickerProps={{
            selectedDays: selectedDays,
            disabledDays: fromDate && { before: fromDate },
            numberOfMonths: 2,
            modifiers: modifiers,
            inputClassNames: {
              ...dayPickerInputClassNames,
            },
            initialMonth: fromDate,
          }}
          onDayChange={toDatePicker.onChange}
          inputRef={toDatePickerRef}
          errorText={errorText}
        />
      </Flex>

      {!!errorText && errorText !== ' ' && (
        <>
          <Gutter size="xxs" />

          <InputMessage text={errorText} color="red600" />
        </>
      )}
      {helperText ? <InputMessage text={helperText} color="gray500" /> : null}
    </Div>
  );
};

export default DatePickerWithRange;
